import { SetupContext } from "vue";
import { CallbackStoreFacade, Dictionary } from "../../../../types";
import { Deferred } from "../../../../types/util/deferred";
import { coreStoreFeatures } from "./features";

type CoreStoreFeatures = typeof coreStoreFeatures;
type GetStoreFeatures<T> = { [K in keyof T]: GetStoreFeature<T[K]> };
export type GetStoreFeature<T> = T extends StoreFeature<infer Feature> ? Feature : never;
export type StoreFeatureTypes<TFeatures> = TFeatures & CoreStoreFeatures;
type StateFeatureTypes<TFeatures, TFeatureExtensions> = GetStoreFeatures<StoreFeatureTypes<TFeatures>> & TFeatureExtensions

export type ReadOnlySet<T> = Omit<Set<T>, "add" | "clear" | "delete">;

export type SkyVueData =
{
    key: string;
    id: string;
    classNames: string;
    features: any;
    hashes: Dictionary<string, string>;
    htmlData: Dictionary<string, string>;
};

export type StateProps =
{
    skyVueData: SkyVueData,
    parent?: State<unknown, unknown, unknown>,
    onAfterFetch?: (state: State<unknown, unknown, unknown>) => void;
}

export enum FetchDataBehavior
{
    Replace,
    Append,
    Prepend
}

export enum ErrorStates
{
    /** Everything is fine! */
    OK = "OK",
    /** Security error in fetch. */
    NotSecure = "NotSecure",
    /** ISE in fetch. */
    ServerError = "ServerError",
    /** Security error when rendering the SkyVue on the server. Refreshing will never succeed and most features are missing. */
    FatalNotSecure = "FatalNotSecure",
}

export type StoreCollection =
{
    [key: string]: Store<any, any, any>
};

export type Store<TFeatures, TSetupStore, TSetupState> =
{
    key: string;
    symbolKey: Symbol,
    setup: StoreSetup<TFeatures, TSetupState>;
    features: StoreFeatureTypes<TFeatures>;
    states: Map<string, State<TFeatures, TSetupState, any, any, any>>;
    awaitedStates: Map<string, Deferred<State<TFeatures, TSetupState, any, any, any>>>;
    addState<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any>>(props: StateProps, context: SetupContext): State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>;
    getState<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any>>(id: string): State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>;
    hasState(id: string): boolean;
    getStateAsync<TFeatureExtensions, TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any>>(id: string): Promise<State<TFeatures, TSetupState, TFeatureExtensions, TDataType, TNonPageable>>;
    deleteState(id: string): void;
} & TSetupStore;

export type State<TFeatures, TSetupState, TFeatureExtensions, TDataType extends object = Dictionary<string, any>, TNonPageable extends object = Dictionary<string, any>> =
{
    features: StateFeatureTypes<TFeatures, TFeatureExtensions>;
    data: Data<TDataType, TNonPageable>;
    hashes: HashData;
    /** A reference to the skyvue's parent state, if any. Use this sparingly. Making assumptions about the parent skyvue leads to tightly coupled code,
     * and since parents can reference children and children can reference parents, there are lots of opportunities for infinite watch() loops.
     */
    parent?: Readonly<State<unknown, unknown, unknown>> | undefined;
    /** A list of the skyvue's skyvue children. */
    children: readonly State<unknown, unknown, unknown>[];
    readonly errorState: ErrorStates;
    readonly errorUserMessage: string;
    /** Clear data and run fetch. Equivalent to fetch(FetchDataBehavior.Replace). */
    refresh(): Promise<void>;
    onRefresh: CallbackStoreFacade<() => void>;
    /** The key of the store this skyvue state belongs to. */
    readonly storeKey: string;
    /** The ID this skyvue was registered with. */
    readonly stateId: string;
} & TSetupState;

export type RowData<TDataType extends object = Dictionary<string, any>> = { [Property in keyof TDataType]: TDataType[Property][] };
interface HashData extends Dictionary<string, string> { };

export type FetchOptions =
{
    fetchDataBehavior: FetchDataBehavior;
    ajaxSettings?: JQuery.AjaxSettings;
};

export interface Data<TDataType extends Dictionary<string, any>, TNonPageable extends Dictionary<string, any> = {}>
{
    /** True if the initial fetch is complete and data is not currently being fetched. */
    isLoaded: boolean;
    /** True if the initial fetch is complete. */
    isInitiallyLoaded: boolean;
    /** True if the chart has noaded and the series data is empty. */
    hasNoData: boolean;
    /** True if fetching failed with an exception. */
    readonly isErrored: boolean;
    /** Pageable data values, such as rows in a browse. */
    values: TDataType[];
    /** Aggregate, non-pageable values, such as the record count. */
    nonPageableValues: TNonPageable;
    /** List of field names in order. */
    sortedFields: string[];
    /** Number of rows in @see values. */
    readonly length: number;
    /** Get the index of the row with the given ID.  Returns -1 if no row was found with that ID.  */
    getIndexByIdentifyingField(value: any): number;
    /** Get the values of the row with the given ID.  Returns undefined if no row was found with that ID.  */
    getRowByIdentifyingField(value: any): TDataType | undefined;
    /**computed value that returns collection of postData*/
    postData(): Record<string, any>;
    /** Array of row IDs. */
    identifyingFieldValues: any[];
    fetch(fetchOptions: FetchOptions, postData?: { [key: string]: any }): JQuery.Promise<Dictionary<string, Array<any>> | undefined>;
    fetch(fetchDataBehavior: FetchDataBehavior, postData?: { [key: string]: any }): JQuery.Promise<Dictionary<string, Array<any>> | undefined>;
    /**
     * A callback to run every time a fetch completes. Fetched data cannot be modified by this callback.
     * Note that, because the initial fetch can complete before all components have loaded, this may not be called. Make sure to check `isInitiallyLoaded`
     * and run your fetch logic on first load, if necessary.
     */
    onAfterFetch: CallbackStoreFacade<() => void>;
    readonly canRefresh: boolean;
};

type StoreSetup<TFeatures, TSetupStore> = (id: string, storeState: State<TFeatures, any, any, any>) => TSetupStore;

export type StoreFeature<Feature> =
{
    getPostData?(state: Feature, storeState: State<{}, {}, {}, {}>): Dictionary<string, any>
};
